import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
	Box,
	Typography,
	List,
	ListItem,
	ListItemButton,
	ListItemText,
	Paper,
	CircularProgress,
	Alert,
	IconButton,
	Tooltip,
} from '@mui/material';
import {
	Description as DocIcon,
	Download as DownloadIcon,
	Folder as FolderIcon,
	ChevronRight as ChevronRightIcon,
	ExpandMore as ExpandMoreIcon,
	ZoomIn as ZoomInIcon,
	ZoomOut as ZoomOutIcon,
	ZoomOutMap as ZoomOutMapIcon,
} from '@mui/icons-material';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import DOMPurify from 'dompurify';
import mermaid from 'mermaid';
import Panzoom from '@panzoom/panzoom';
import { apiService } from '../services/api';
import {
	buildRootNodes,
	buildFileTree,
	mergeTree,
	updateNodeExpanded,
	setNodeChildren,
	findNodeByPath,
	sortWorkspaceFiles,
} from './fileTreeUtils';
import type { FileNode } from './fileTreeUtils';

interface OutputDocsProps {
	containerAppUrl: string;
	userId: string;
}

type RenderMode = 'markdown' | 'mermaid' | 'html' | 'code';

export function getRenderMode(filePath: string): RenderMode {
	const filename = filePath.split('/').pop() ?? '';
	const ext = (filename.split('.').pop() ?? '').toLowerCase();
	switch (ext) {
		case 'md': return 'markdown';
		case 'mmd': return 'mermaid';
		case 'html': return 'html';
		default: return 'code';
	}
}

const MermaidDiagram: React.FC<{ content: string }> = ({ content }) => {
	const containerRef = useRef<HTMLDivElement>(null);
	const panzoomRef = useRef<ReturnType<typeof Panzoom> | null>(null);
	const cleanupRef = useRef<(() => void) | null>(null);
	const initialScaleRef = useRef<number>(1);
	const [renderError, setRenderError] = useState<string | null>(null);
	const [scale, setScale] = useState(1);

	useEffect(() => {
		const container = containerRef.current;
		if (!container) return;

		// Tear down any previous panzoom instance before re-rendering
		cleanupRef.current?.();

		let cancelled = false;
		mermaid.initialize({ startOnLoad: false, theme: 'neutral' });

		const id = `mermaid-${Math.random().toString(36).slice(2, 9)}`;
		mermaid.render(id, content)
			.then(({ svg }) => {
				/* istanbul ignore next -- cancelled when content changes before promise resolves */
				if (cancelled) return;

				container.innerHTML = svg;
				const svgEl = container.querySelector('svg');
				/* istanbul ignore next -- mermaid always returns a valid SVG element */
				if (!svgEl) return;

				// Force the SVG to fill the container width so that panzoom scale=1
				// always means "fit to container". Mermaid sets an inline max-width
				// equal to the diagram's pixel width (e.g. 6758px), which we must
				// clear so the SVG doesn't overflow the panel at natural size.
				svgEl.removeAttribute('width');
				svgEl.removeAttribute('height');
				svgEl.style.width = '100%';
				svgEl.style.height = 'auto';
				svgEl.style.maxWidth = 'none';
				// Clear any leftover transform from a prior panzoom instance so
				// panzoom reads the identity matrix and starts at scale=1.
				svgEl.style.transform = '';
				svgEl.style.transformOrigin = '';
				initialScaleRef.current = 1;

				// minScale 0.5  — prevents shrinking below half the fit-to-width view
			// maxScale 100  — SVG is vector so high zoom is cheap; "Fit to width" always
			//                 recovers. Very wide diagrams (e.g. 6758px in a 750px panel)
			//                 reach native resolution at ~900%, so 10,000% leaves plenty
			//                 of headroom for any diagram without a meaningful cost.
			// step 0.3      — each button click is a 30% increment
			const pz = Panzoom(svgEl, { maxScale: 100, minScale: 0.5, step: 0.3 });
				panzoomRef.current = pz;

				const handleChange = (e: Event) => {
					const { scale: s } = (e as CustomEvent<{ scale: number }>).detail;
					setScale(s);
				};
				svgEl.addEventListener('panzoomchange', handleChange);

				// Explicitly snap to scale=1 so any inherited transform from a prior
				// panzoom instance (which destroy() leaves on the element) is cleared.
				pz.zoom(1, { animate: false });
				setScale(1);

				// passive: false lets panzoom call preventDefault() to block page scroll during wheel-zoom
				const wheelHandler = (e: WheelEvent) => pz.zoomWithWheel(e);
				container.addEventListener('wheel', wheelHandler as unknown as EventListener, { passive: false });

				cleanupRef.current = () => {
					svgEl.removeEventListener('panzoomchange', handleChange);
					container.removeEventListener('wheel', wheelHandler as unknown as EventListener);
					pz.destroy();
					panzoomRef.current = null;
				};
			})
			.catch((err: unknown) => {
				// Mermaid appends a temporary #d{id} element to document.body during
				// rendering. On syntax errors it rejects but doesn't remove that element,
				// leaving its error SVG visible at the bottom of the page.
				document.getElementById(`d${id}`)?.remove();
				if (!cancelled) {
					setRenderError(err instanceof Error ? err.message : 'Failed to render diagram');
				}
			});

		return () => {
			cancelled = true;
			cleanupRef.current?.();
			cleanupRef.current = null;
		};
	}, [content]);

	const handleZoomIn = useCallback(() => { panzoomRef.current?.zoomIn(); }, []);
	const handleZoomOut = useCallback(() => { panzoomRef.current?.zoomOut(); }, []);
	const handleZoomReset = useCallback(() => {
		panzoomRef.current?.reset();
		setScale(1);
	}, []);

	if (renderError) {
		return <Alert severity="error">{renderError}</Alert>;
	}

	return (
		<Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
			{/* Toolbar — stays pinned at the top even when the content area has no scroll */}
			<Box
				sx={{
					flexShrink: 0,
					display: 'flex',
					alignItems: 'center',
					gap: 0.5,
					px: 1,
					py: 0.5,
					justifyContent: 'flex-end',
					borderBottom: '1px solid',
					borderColor: 'divider',
					backgroundColor: 'background.paper',
				}}
			>
				<Tooltip title="Zoom out">
					<IconButton aria-label="Zoom out" size="small" onClick={handleZoomOut}>
						<ZoomOutIcon fontSize="small" />
					</IconButton>
				</Tooltip>
				<Typography variant="caption" sx={{ minWidth: '3em', textAlign: 'center' }}>
					{Math.round(scale * 100)}%
				</Typography>
				<Tooltip title="Zoom in">
					<IconButton aria-label="Zoom in" size="small" onClick={handleZoomIn}>
						<ZoomInIcon fontSize="small" />
					</IconButton>
				</Tooltip>
				<Tooltip title="Fit to width">
					<IconButton aria-label="Fit to width" size="small" onClick={handleZoomReset}>
						<ZoomOutMapIcon fontSize="small" />
					</IconButton>
				</Tooltip>
			</Box>
			{/* Panzoom canvas — fills all remaining height, clips panned content */}
		<Box
			sx={{
				flex: 1,
				overflow: 'hidden',
				cursor: 'grab',
				'&:active': { cursor: 'grabbing' },
			}}
		>
			{/* Fill the full canvas so panzoom's overflow:hidden boundary matches the
			    panel height, not just the SVG's collapsed intrinsic height */}
			<div ref={containerRef} data-testid="mermaid-diagram" style={{ width: '100%', height: '100%' }} />
		</Box>
		</Box>
	);
};

function renderDocContent(mode: RenderMode, content: string, filePath: string): React.ReactNode {
	if (mode === 'markdown') {
		return (
			<Box
				sx={{
					'& h1': { typography: 'h4', mb: 2, mt: 0 },
					'& h2': { typography: 'h5', mb: 1.5, mt: 3 },
					'& h3': { typography: 'h6', mb: 1, mt: 2.5 },
					'& p': { mb: 1.5, lineHeight: 1.7 },
					'& ul, & ol': { pl: 3, mb: 1.5 },
					'& li': { mb: 0.5 },
					'& code': {
						fontFamily: 'monospace',
						fontSize: '0.875em',
						backgroundColor: 'action.hover',
						px: 0.5,
						borderRadius: 0.5,
					},
					'& pre': {
						backgroundColor: 'action.hover',
						p: 2,
						borderRadius: 1,
						overflow: 'auto',
						mb: 1.5,
						'& code': { backgroundColor: 'transparent', px: 0 },
					},
					'& blockquote': {
						borderLeft: '4px solid',
						borderColor: 'divider',
						pl: 2,
						ml: 0,
						color: 'text.secondary',
					},
					'& hr': { my: 2, borderColor: 'divider' },
					'& a': { color: 'primary.main' },
					'& table': { borderCollapse: 'collapse', width: '100%', mb: 1.5 },
					'& th, & td': { border: '1px solid', borderColor: 'divider', p: 1 },
					'& th': { backgroundColor: 'action.hover', fontWeight: 'bold' },
				}}
			>
				<ReactMarkdown remarkPlugins={[remarkGfm]}>{content}</ReactMarkdown>
			</Box>
		);
	}

	if (mode === 'mermaid') {
		return <MermaidDiagram content={content} />;
	}

	if (mode === 'html') {
		return (
			<iframe
				sandbox=""
				srcDoc={DOMPurify.sanitize(content, { WHOLE_DOCUMENT: true })}
				style={{ width: '100%', height: '100%', border: 'none' }}
				title={filePath.split('/').pop() ?? 'HTML document'}
			/>
		);
	}

	return (
		<Box
			component="pre"
			data-testid="code-viewer"
			sx={{
				fontFamily: 'monospace',
				fontSize: '0.875rem',
				overflow: 'auto',
				m: 0,
				whiteSpace: 'pre-wrap',
				wordBreak: 'break-word',
			}}
		>
			{content}
		</Box>
	);
}

/**
 * Filters recursive workspace listing to only entries under output-docs/{userId}/,
 * builds a nested tree from the full paths, then returns only the subtree rooted
 * at the user's directory — so the tree starts with the user's own content, not
 * the output-docs/userId hierarchy. Each FileNode retains its full path for API calls.
 */
function filterAndBuildOutputDocsTree(
	files: Array<{ name: string; type: 'file' | 'directory'; path: string }>,
	userId: string,
): FileNode[] {
	const prefix = `output-docs/${userId}/`;
	const userDirEntries = files.filter(f => f.path.startsWith(prefix));
	const fullTree = buildFileTree(userDirEntries);

	// buildFileTree created the hierarchy output-docs > {userId} > ...
	// Drill down to the user's directory node and return its children as root.
	const outputDocsNode = fullTree.find(n => n.name === 'output-docs');
	if (!outputDocsNode?.children) return [];
	const userNode = outputDocsNode.children.find(n => n.name === userId);
	return userNode?.children ?? [];
}

export const OutputDocs: React.FC<OutputDocsProps> = ({ containerAppUrl, userId }) => {
	const [fileTree, setFileTree] = useState<FileNode[]>([]);
	const [loadingDirs, setLoadingDirs] = useState<Set<string>>(new Set());
	const [selectedDoc, setSelectedDoc] = useState<string | null>(null);
	const [content, setContent] = useState<string | null>(null);
	const [loading, setLoading] = useState(true);
	const [contentLoading, setContentLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [contentError, setContentError] = useState<string | null>(null);
	const [downloading, setDownloading] = useState(false);

	/**
	 * Phase 1: fetch only the user's output-docs directory (single-level) so the
	 * panel becomes responsive almost immediately.
	 */
	const loadRootLevel = useCallback(async () => {
		/* istanbul ignore next -- component is not mounted without these props */
		if (!containerAppUrl || !userId) return;

		setLoading(true);
		setError(null);

		try {
			const response = await apiService.getOutputDocs(containerAppUrl, userId);
			if (response.success && response.data) {
				const files = response.data.files ?? [];
				setFileTree(buildRootNodes(files));
			} else {
				setError(response.error || 'Failed to load documents');
			}
		} catch (err) {
			setError(err instanceof Error ? err.message : 'Failed to load documents');
		} finally {
			setLoading(false);
		}
	}, [containerAppUrl, userId]);

	/**
	 * Phase 2: silently fetch the full recursive workspace listing in the background,
	 * filter to only this user's output-docs entries, build a nested tree, and merge
	 * it in — preserving any expanded state.
	 */
	const prefetchFullTree = useCallback(async () => {
		/* istanbul ignore next -- component is not mounted without these props */
		if (!containerAppUrl || !userId) return;

		try {
			const response = await apiService.getWorkspaceFiles(containerAppUrl);
			if (response.success && response.data && response.data.files && response.data.files.length > 0) {
				const fullTree = filterAndBuildOutputDocsTree(response.data.files, userId);
				setFileTree(prev => mergeTree(prev, fullTree));
			}
		} catch {
			// Prefetch failures are silent — root-level entries and on-demand loading still work
		}
	}, [containerAppUrl, userId]);

	/**
	 * Phase 3 (on-demand): fetch a single directory level when the user expands a
	 * folder whose children haven't been loaded yet.
	 */
	const loadDirectoryChildren = useCallback(async (dirPath: string) => {
		/* istanbul ignore next -- component is not mounted without this prop */
		if (!containerAppUrl) return;

		setLoadingDirs(prev => new Set(prev).add(dirPath));
		try {
			const response = await apiService.getWorkspaceFiles(containerAppUrl, dirPath);
			const children: FileNode[] = response.success && response.data && response.data.files
				? sortWorkspaceFiles(response.data.files).map(file => ({
					name: file.name,
					type: file.type,
					path: file.path,
					children: file.type === 'directory' ? [] : undefined,
					expanded: false,
					childrenLoaded: file.type !== 'directory',
				}))
				: [];
			setFileTree(prev => setNodeChildren(prev, dirPath, children));
		} catch {
			setFileTree(prev => setNodeChildren(prev, dirPath, []));
		} finally {
			setLoadingDirs(prev => {
				const next = new Set(prev);
				next.delete(dirPath);
				return next;
			});
		}
	}, [containerAppUrl]);

	const loadContent = useCallback(async (docPath: string) => {
		/* istanbul ignore next -- component is not mounted without this prop */
		if (!containerAppUrl) return;

		setContentLoading(true);
		setContentError(null);
		setContent(null);

		try {
			const response = await apiService.getWorkspaceFileContent(containerAppUrl, docPath);
			if (response.success && response.data) {
				setContent(response.data.content);
			} else {
				setContentError(response.error || 'Failed to load document content');
			}
		} catch (err) {
			setContentError(err instanceof Error ? err.message : 'Failed to load document content');
		} finally {
			setContentLoading(false);
		}
	}, [containerAppUrl]);

	useEffect(() => {
		const init = async () => {
			await loadRootLevel();
			void prefetchFullTree();
		};
		void init();
	}, [loadRootLevel, prefetchFullTree]);

	useEffect(() => {
		if (selectedDoc) {
			void loadContent(selectedDoc);
		} else {
			setContent(null);
			setContentError(null);
		}
	}, [selectedDoc, loadContent]);

	const handleFileClick = (nodePath: string, type: 'file' | 'directory') => {
		if (type === 'file') {
			setSelectedDoc(nodePath);
		} else {
			setFileTree(prev => updateNodeExpanded(prev, nodePath));
			const node = findNodeByPath(fileTree, nodePath);
			if (node && !node.childrenLoaded) {
				loadDirectoryChildren(nodePath);
			}
		}
	};

	const handleDownload = async () => {
		/* istanbul ignore next -- button is only rendered when selectedDoc is set and component requires containerAppUrl */
		if (!selectedDoc || !containerAppUrl) return;

		setDownloading(true);
		try {
			await apiService.downloadOutputDoc(containerAppUrl, selectedDoc);
		} catch (err) {
			setContentError(err instanceof Error ? err.message : 'Download failed');
		} finally {
			setDownloading(false);
		}
	};

	const renderFileTree = (nodes: FileNode[], level = 0): React.ReactNode =>
		nodes.map(node => (
			<React.Fragment key={node.path}>
				<ListItem disablePadding sx={{ pl: level * 2 }}>
					<ListItemButton
						selected={node.type === 'file' && node.path === selectedDoc}
						onClick={() => handleFileClick(node.path, node.type)}
						dense
						sx={{
							'&.Mui-selected': {
								backgroundColor: 'action.selected',
							},
						}}
					>
						{node.type === 'directory' ? (
							node.expanded ? (
								<ExpandMoreIcon sx={{ mr: 1, fontSize: '1rem' }} />
							) : (
								<ChevronRightIcon sx={{ mr: 1, fontSize: '1rem' }} />
							)
						) : (
							<Box sx={{ width: 24, mr: 1 }} />
						)}
						{node.type === 'directory' ? (
							<FolderIcon sx={{ mr: 1, fontSize: '1.2rem', color: 'text.secondary' }} />
						) : (
							<DocIcon sx={{ mr: 1, fontSize: '1.2rem', color: 'text.secondary', flexShrink: 0 }} />
						)}
						<ListItemText
							primary={node.name}
							slotProps={{
								primary: { fontSize: '0.9rem' },
							}}
						/>
						{node.type === 'directory' && loadingDirs.has(node.path) && (
							<CircularProgress size={12} sx={{ ml: 1 }} />
						)}
					</ListItemButton>
				</ListItem>
				{node.type === 'directory' && node.expanded && node.children && !loadingDirs.has(node.path) && (
					<>{renderFileTree(node.children, level + 1)}</>
				)}
			</React.Fragment>
		));

	const selectedRenderMode = selectedDoc ? getRenderMode(selectedDoc) : 'code';
	const isViewerMode = selectedRenderMode === 'mermaid' || selectedRenderMode === 'html';

	return (
		<Box sx={{ flex: 1, display: 'flex', height: '100%', overflow: 'hidden' }}>
			{/* Document list panel */}
			<Paper
				sx={{
					width: 280,
					borderRight: '1px solid',
					borderColor: 'divider',
					display: 'flex',
					flexDirection: 'column',
					overflow: 'hidden',
					borderRadius: 0,
				}}
			>
				<Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
					<Typography variant="h6" sx={{ fontWeight: 'bold' }}>
						My Documents
					</Typography>
				</Box>
				<Box sx={{ flex: 1, overflow: 'auto' }}>
					{loading ? (
						<Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
							<CircularProgress size={24} />
						</Box>
					) : error ? (
						<Box sx={{ p: 2 }}>
							<Alert severity="error">{error}</Alert>
						</Box>
					) : fileTree.length === 0 ? (
						<Box sx={{ p: 2 }}>
							<Typography variant="body2" color="text.secondary">
								No documents yet. Ask the agent to create a summary or analysis document.
							</Typography>
						</Box>
					) : (
						<List sx={{ p: 0 }}>
							{renderFileTree(fileTree)}
						</List>
					)}
				</Box>
			</Paper>

			{/* Document content panel */}
			<Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
				<Paper
					sx={{
						flex: 1,
						display: 'flex',
						flexDirection: 'column',
						overflow: 'hidden',
						borderRadius: 0,
					}}
				>
					<Box
						sx={{
							p: 2,
							borderBottom: '1px solid',
							borderColor: 'divider',
							display: 'flex',
							alignItems: 'center',
							justifyContent: 'space-between',
						}}
					>
						<Typography variant="h6" sx={{ fontWeight: 'bold', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
							{selectedDoc ? selectedDoc.split('/').pop() : 'No document selected'}
						</Typography>
						{selectedDoc && (
							<Tooltip title="Download">
								<span>
									<IconButton
										aria-label="Download"
										onClick={handleDownload}
										disabled={downloading || contentLoading}
										size="small"
									>
										{downloading ? <CircularProgress size={16} /> : <DownloadIcon fontSize="small" />}
									</IconButton>
								</span>
							</Tooltip>
						)}
					</Box>
				{/* Canvas modes (mermaid, html) fill the full panel — no padding, no scroll */}
			{(() => {
				const mode = selectedDoc ? getRenderMode(selectedDoc) : null;
				return (
						<Box
							sx={{
								flex: 1,
								overflow: isViewerMode ? 'hidden' : 'auto',
								p: isViewerMode ? 0 : 3,
								...(isViewerMode && { display: 'flex', flexDirection: 'column' }),
							}}
						>
							{!selectedDoc ? (
								<Typography variant="body2" color="text.secondary">
									Select a document from the list to view its contents.
								</Typography>
							) : contentLoading ? (
								<Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
									<CircularProgress size={24} />
								</Box>
							) : contentError ? (
								<Alert severity="error">{contentError}</Alert>
							) : content !== null ? (
								renderDocContent(mode!, content, selectedDoc)
							) : null}
						</Box>
					);
				})()}
				</Paper>
			</Box>
		</Box>
	);
};
